import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';
import IconDesktop from '@/assets/icons/icon-desktop.svg?react';
import IconTablet from '@/assets/icons/icon-tablet.svg?react';
import IconMobile from '@/assets/icons/icon-mobile.svg?react';
import IconFullscreen from '@/assets/icons/icon-fullscreen.svg?react';
import IconDesktopFilled from '@/assets/icons/icon-desktop-filled.svg?react';
import IconTabletFilled from '@/assets/icons/icon-tablet-filled.svg?react';
import IconMobileFilled from '@/assets/icons/icon-mobile-filled.svg?react';
import IconFullscreenFilled from '@/assets/icons/icon-fullscreen-filled.svg?react';
import clsx from 'clsx';
import { Tooltip } from '@/components/tooltips/Tooltip/Tooltip';
import { useAppSelector } from '@/store/hooks';
import type { Breakpoint } from '@builder4/shared/types/layout';
import {
  selectBreakpoint,
  selectPageWidth,
  selectIsFullscreenPreview,
} from '@/store/system/selectors';
import { NumberField } from '@/components/inspector-fields/NumberField/NumberField';
import useBreakpoint from '@/hooks/useBreakpoint';
import { ROOT_NODE, useEditor } from '@craftjs/core';
import { getElementDimensionsWithPadding } from '@/utils/numToMeasurement';

interface ResponsivePreviewNavProps {
  state?: Breakpoint;
  className?: string;
  frameRef: HTMLDivElement | null;
}

const breakpoints: Breakpoint[] = ['desktop', 'tablet', 'mobile'];

const icons = {
  desktop: {
    active: IconDesktopFilled,
    inactive: IconDesktop,
  },
  tablet: {
    active: IconTabletFilled,
    inactive: IconTablet,
  },
  mobile: {
    active: IconMobileFilled,
    inactive: IconMobile,
  },
};

export const ResponsivePreviewNav: React.FC<ResponsivePreviewNavProps> = ({ state, className }) => {
  const storeBreakpoint = useAppSelector(selectBreakpoint);
  const isFullscreenPreview = useAppSelector(selectIsFullscreenPreview);
  const { switchPreviewBreakpoint, toggleFullscreenPreview } = useBreakpoint();
  const [selectedBreakpoint, setSelectedBreakpoint] = useState<Breakpoint>(
    state || storeBreakpoint,
  );
  const [highlightStyle, setHighlightStyle] = useState({ width: 0, left: 0 });
  const [isAnimated, setIsAnimated] = useState(false);
  const pageWidth = useAppSelector(selectPageWidth);
  const optionRefs = useRef<Record<string, HTMLButtonElement | null>>({
    desktop: null,
    tablet: null,
    mobile: null,
  });
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const source = state ?? storeBreakpoint;
    if (source !== selectedBreakpoint) {
      setSelectedBreakpoint(source);
    }
  }, [state, storeBreakpoint]);

  const handleBreakpointClick = (newSize: Breakpoint) => {
    if (newSize !== selectedBreakpoint) {
      setSelectedBreakpoint(newSize);
      switchPreviewBreakpoint(newSize);
    }
  };

  useLayoutEffect(() => {
    const activeEl = optionRefs.current[selectedBreakpoint];
    if (activeEl) {
      const newHighlightStyle = {
        width: activeEl.offsetWidth || 32,
        left: activeEl.offsetLeft || 0,
      };

      if (
        highlightStyle.width !== newHighlightStyle.width ||
        highlightStyle.left !== newHighlightStyle.left
      ) {
        setHighlightStyle(newHighlightStyle);
        setIsAnimated(true);
      }
    }
  }, [selectedBreakpoint]);

  const { query } = useEditor();
  const root = query.node(ROOT_NODE).get();
  let height = 1200;
  if (root?.dom) {
    height = getElementDimensionsWithPadding(root?.dom).height;
  }
  return (
    <div
      className={clsx(
        'bg-neutral-0 inline-flex items-center justify-start gap-2 rounded-xl px-2',
        className,
      )}
    >
      {!isFullscreenPreview && (
        <div className="relative flex h-8 items-center overflow-hidden rounded-md bg-neutral-50 shadow-[inset_0px_0px_2px_0px_rgba(0,0,0,0.12)]">
          <div
            className={`absolute top-0 z-10 flex h-full w-8 items-center justify-center rounded-md border border-neutral-100 bg-white text-indigo-500 ${
              isAnimated ? 'bg-white transition-all duration-300' : ''
            }`}
            style={{
              width: `${highlightStyle.width}px`,
              transform: `translateX(${highlightStyle.left}px)`,
            }}
            onTransitionEnd={() => setIsAnimated(false)}
          ></div>

          {breakpoints.map((breakpoint) => {
            const isActive = selectedBreakpoint === breakpoint;
            const Icon = icons[breakpoint].inactive;
            const ActiveIcon = icons[breakpoint].active;

            return (
              <button
                key={breakpoint}
                ref={(el) => {
                  optionRefs.current[breakpoint] = el;
                }}
                onClick={() => handleBreakpointClick(breakpoint)}
                data-active={isActive}
                data-qa-selector={`responsive-preview-${breakpoint}-button`}
                aria-label={`Preview: ${breakpoint}`}
                className={`group text-button-sm relative z-10 h-8 w-8 cursor-pointer p-[7px] capitalize ${
                  isActive ? 'text-indigo-500' : 'text-neutral-400'
                } hover:text-indigo-500`}
              >
                <span className="relative flex items-center justify-center">
                  {isActive ? (
                    <ActiveIcon className="h-5 w-5 text-indigo-500" />
                  ) : (
                    <>
                      <span className="absolute opacity-0 transition-opacity group-hover:opacity-100">
                        <ActiveIcon className="h-5 w-5 text-indigo-500" />
                      </span>
                      <span className="transition-opacity group-hover:opacity-0">
                        <Icon className="h-5 w-5 text-neutral-400 group-hover:text-indigo-500" />
                      </span>
                    </>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      )}

      <NumberField
        className="max-w-[216px]"
        classNameInput="pl-6 pr-4 w-[68px]"
        max={16800}
        min={0}
        onChange={() => {}}
        preLetter="W"
        step={1}
        suffix=""
        text=""
        value={isFullscreenPreview ? viewportWidth : (pageWidth ?? 0)}
        arrows={false}
        disabled={isFullscreenPreview}
        data-qa-selector="responsive-preview-width-field"
      />
      <NumberField
        className="max-w-[216px]"
        classNameInput="pl-6 pr-4 w-[68px]"
        max={16800}
        min={0}
        onChange={() => {}}
        preLetter="H"
        step={1}
        suffix=""
        text=""
        value={height}
        arrows={false}
        disabled={isFullscreenPreview}
        data-qa-selector="responsive-preview-height-field"
      />
      <Tooltip textFirst="Toggle fullscreen" textSecond="" className="flex items-center">
        <button
          onClick={() => toggleFullscreenPreview()}
          data-active={isFullscreenPreview}
          data-qa-selector="responsive-preview-fullscreen-button"
          aria-label="Toggle fullscreen"
          aria-pressed={isFullscreenPreview}
          className="cursor-pointer"
        >
          <span
            className={`flex h-9 w-9 items-center justify-center rounded-md transition-all ${
              isFullscreenPreview ? 'bg-indigo-500' : 'text-neutral-500 hover:bg-neutral-100'
            }`}
          >
            {isFullscreenPreview ? <IconFullscreenFilled /> : <IconFullscreen />}
          </span>
        </button>
      </Tooltip>
    </div>
  );
};
